import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { motion, AnimatePresence } from "framer-motion";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../lib/firebase";

import logoColor from "/logo/favicon.svg";
import logoWhite from "/logo/faviconWhite.svg";

export const Navbar = () => {
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [events, setEvents] = useState([]);
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const auth = getAuth();
  const menuRef = useRef(null);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const idTokenResult = await currentUser.getIdTokenResult();
          setIsAdmin(idTokenResult.claims.role === "admin");
        } catch (error) {
          console.error("Error checking admin role:", error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
        setIsResultsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const snapshot = await getDocs(collection(db, "semuaKegiatan"));
        const eventList = snapshot.docs
          .map((eventDoc) => ({ id: eventDoc.id, ...eventDoc.data() }))
          .filter((event) => String(event.id || "").toUpperCase() !== "JMM")
          .sort((a, b) =>
            String(a.title || a.id).localeCompare(String(b.title || b.id))
          );
        setEvents(eventList);
      } catch (error) {
        console.error("Failed to fetch events for navbar results menu:", error);
      }
    };

    fetchEvents();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error saat logout:", error.message);
    }
  };

  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const closeMenu = () => setMenuOpen(false);

  const shouldShowWhiteBg = isHomePage ? scrolled : true;
  const textColorClass = shouldShowWhiteBg ? "text-slate-800" : "text-white";
  const hoverColorClass = "hover:text-slate-800 hover:border-b-2 hover:border-slate-800";
  const activeColorClass = "text-slate-600 border-b-2 border-slate-600";
  const logoSrc = shouldShowWhiteBg ? logoColor : logoWhite;
  const logoTextColor = shouldShowWhiteBg ? "text-slate-600" : "text-white";

  return (
    <>
      {/* NAVBAR UTAMA */}
      <nav
        className={`w-full fixed top-0 left-0 z-50 py-3 transition-all duration-300 backdrop-blur-sm ${
          shouldShowWhiteBg ? "bg-white/95 shadow-lg" : "bg-transparent"
        }`}
      >
        <div
          className="container mx-auto px-4 flex items-center justify-between"
          ref={menuRef}
        >
          <div className="flex-1 md:flex md:items-center">
            <Link
              to="/"
              className="flex items-center space-x-3 text-2xl font-bold"
            >
              <motion.img
                src={logoSrc}
                alt="Logo"
                className="w-12 h-12 md:w-14"
                key={logoSrc}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              />
              <span
                className={`hidden md:block text-xl font-bold ${logoTextColor}`}
              >
                MOUNTAIN MARATHON FESTIVAL
              </span>
            </Link>
          </div>

          <span
            className={`block md:hidden absolute left-1/2 -translate-x-1/2 text-lg font-bold ${logoTextColor}`}
          >
            MMF
          </span>

          <div className="hidden md:flex items-center space-x-8 text-base uppercase font-medium">
            <Link
              to="/"
              className={`${
                activeMenu === "home"
                  ? activeColorClass
                  : textColorClass
              } ${hoverColorClass} pb-1`}
              onClick={() => setActiveMenu("home")}
            >
              Home
            </Link>
            <Link
              to="/Regulations"
              className={`${
                activeMenu === "news"
                  ? activeColorClass
                  : textColorClass
              } ${hoverColorClass} pb-1`}
              onClick={() => setActiveMenu("news")}
            >
              Regulations
            </Link>
            <Link
              to="/checkyourname"
              className={`${
                activeMenu === "checkname"
                  ? activeColorClass
                  : textColorClass
              } ${hoverColorClass} pb-1`}
              onClick={() => setActiveMenu("checkname")}
            >
              Check Your Name
            </Link>
            <Link
              to="/Kontak"
              className={`${
                activeMenu === "contact"
                  ? activeColorClass
                  : textColorClass
              } ${hoverColorClass} pb-1`}
              onClick={() => setActiveMenu("contact")}
            >
              Contact
            </Link>
            <div
              className="relative"
              onMouseEnter={() => setIsResultsOpen(true)}
              onMouseLeave={() => setIsResultsOpen(false)}
            >
              <button
                type="button"
                className={`${
                  activeMenu === "results"
                    ? activeColorClass
                    : textColorClass
                } ${hoverColorClass} pb-1 uppercase inline-flex items-center gap-1`}
                onClick={() => {
                  setActiveMenu("results");
                  setIsResultsOpen(true);
                }}
                aria-haspopup="menu"
                aria-expanded={isResultsOpen}
              >
                Results
                <span className="text-xs">▾</span>
              </button>

              {isResultsOpen && (
                <div className="absolute top-full left-0 pt-2 min-w-[220px] z-[60]">
                  <div className="bg-white border border-slate-200 rounded-xl shadow-xl py-2">
                    <Link
                      to="/results/JMM"
                      className="flex items-center justify-between px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      onClick={() => setIsResultsOpen(false)}
                    >
                      <span>JMM 2026</span>
                    
                    </Link>
                    {events.length === 0 ? (
                      <Link
                        to="/results"
                        className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                        onClick={() => setIsResultsOpen(false)}
                      >
                        All Events
                      </Link>
                    ) : (
                      events.map((eventOption) => (
                        <Link
                          key={eventOption.id}
                          to={`/results/${eventOption.id}`}
                          className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          onClick={() => setIsResultsOpen(false)}
                        >
                          {String(eventOption.id || "").toUpperCase()} 2025
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {!user ? (
              <Link
                to="/Login"
                className={`${textColorClass} ${hoverColorClass} pb-1`}
              >
                Login
              </Link>
            ) : (
              <div className="flex items-center space-x-6 border-l border-slate-300 pl-6">
                <Link
                  to="/MyOrders"
                  className={`${textColorClass} ${hoverColorClass}`}
                >
                  My Orders
                </Link>
                <Link
                  to="/Profile"
                  className={`${textColorClass} ${hoverColorClass}`}
                >
                  Profile
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className={`${textColorClass} ${hoverColorClass}`}
                  >
                    Admin
                  </Link>
                )}
              </div>
            )}
          </div>

          <button
            onClick={toggleMenu}
            className="md:hidden z-50 relative focus:outline-none"
          >
            <svg
              className={`w-8 h-8 ${
                menuOpen ? "text-slate-800" : textColorClass
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {menuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {/* MOBILE MENU FULLSCREEN - LOGOUT SUDAH RATA TENGAH */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 bg-white z-40 flex flex-col items-center justify-center md:hidden"
          >
            <div className="flex flex-col text-center w-full px-4 uppercase font-medium divide-y divide-slate-200 max-w-lg mx-auto">
              {/* MENU UTAMA */}
              <div className="flex flex-col py-4 space-y-2">
                <Link
                  to="/"
                  className="py-2 text-slate-800 hover:text-slate-600 transition-colors"
                  onClick={closeMenu}
                >
                  HOME
                </Link>
                <Link
                  to="/Regulations"
                  className="py-2 text-slate-800 hover:text-slate-600 transition-colors"
                  onClick={closeMenu}
                >
                  REGULATIONS
                </Link>
                <Link
                  to="/checkyourname"
                  className="py-2 text-slate-800 hover:text-slate-600 transition-colors"
                  onClick={closeMenu}
                >
                  CHECK YOUR NAME
                </Link>
                <Link
                  to="/Kontak"
                  className="py-2 text-slate-800 hover:text-slate-600 transition-colors"
                  onClick={closeMenu}
                >
                  CONTACT
                </Link>
                <div className="py-2">
                  <p className="text-slate-500 text-xs mb-2">RESULTS</p>
                  <div className="flex flex-col gap-1">
                    <Link
                      to="/results/JMM"
                      className="py-1 text-slate-800 hover:text-slate-600 transition-colors"
                      onClick={closeMenu}
                    >
                      JMM 2026
                    </Link>
                    {events.length === 0 ? (
                      <Link
                        to="/results"
                        className="py-1 text-slate-800 hover:text-slate-600 transition-colors"
                        onClick={closeMenu}
                      >
                        All Events
                      </Link>
                    ) : (
                      events.map((eventOption) => (
                        <Link
                          key={eventOption.id}
                          to={`/results/${eventOption.id}`}
                          className="py-1 text-slate-800 hover:text-slate-600 transition-colors"
                          onClick={closeMenu}
                        >
                          {String(eventOption.id || "").toUpperCase()} 2025
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* USER SECTION */}
              {user ? (
                <div className="flex flex-col py-4 space-y-2">
                  <Link
                    to="/Profile"
                    className="py-2 text-slate-800 hover:text-slate-600 transition-colors"
                    onClick={closeMenu}
                  >
                    MY PROFILE
                  </Link>
                  <Link
                    to="/MyOrders"
                    className="py-2 text-slate-800 hover:text-slate-600 transition-colors"
                    onClick={closeMenu}
                  >
                    MY ORDERS
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="py-2 text-slate-800 hover:text-slate-600 transition-colors"
                      onClick={closeMenu}
                    >
                      ADMIN PANEL
                    </Link>
                  )}
                  {/* PERBAIKAN: Hapus text-left → sekarang rata tengah */}
                  <button
                    onClick={() => {
                      handleLogout();
                      closeMenu();
                    }}
                    className="py-2 text-slate-800 hover:text-slate-600 transition-colors"
                  >
                    LOGOUT
                  </button>
                </div>
              ) : (
                <div className="py-4">
                  <Link
                    to="/Login"
                    className="py-2 text-slate-800 hover:text-slate-600 transition-colors"
                    onClick={closeMenu}
                  >
                    LOGIN
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
